import DataTable, {
  type TableColumn,
  type SortOrder
} from "react-data-table-component";
import { useMemo, useCallback } from "react";
import { Typography } from "../atoms/Typography";
import { dataTableCustomStyles } from "../../config/dataTableTheme";
import type { Representative } from "../../types/representative";
import type { RepresentativeSortBy } from "../../services/representativeService";
import { createDataTablePagination } from "./DataTablePagination";

const representativeDetailsTablePagination = createDataTablePagination({
  rowsPerPageOptions: [10, 25, 50]
});

type RepresentativeDetailsTableProps = {
  readonly representatives: Representative[];
  readonly onToggleStatus: (id: string, newStatus: "Active" | "Inactive") => void;
  readonly totalRows: number;
  readonly isLoading: boolean;
  readonly page: number;
  readonly perPage: number;
  readonly onChangePage: (page: number) => void;
  readonly onChangeRowsPerPage: (perPage: number, page: number) => void;
  readonly sortBy?: RepresentativeSortBy;
  readonly sortDir?: "asc" | "desc";
  readonly onSort?: (
    sortBy: RepresentativeSortBy,
    sortDir: "asc" | "desc"
  ) => void;
};

const StatusToggle = ({
  representative,
  onToggleStatus
}: {
  readonly representative: Representative;
  readonly onToggleStatus: (id: string, newStatus: "Active" | "Inactive") => void;
}) => {
  const isActive = representative.status === "Active";
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        role="switch"
        aria-checked={isActive}
        onClick={() =>
          onToggleStatus(representative.id, isActive ? "Inactive" : "Active")
        }
        className={
          isActive
            ? "relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-primary-500 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
            : "relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-300 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
        }
      >
        <span
          className={
            isActive
              ? "pointer-events-none inline-block h-5 w-5 translate-x-5 rounded-full bg-white shadow-sm ring-0 transition-transform"
              : "pointer-events-none inline-block h-5 w-5 translate-x-0 rounded-full bg-white shadow-sm ring-0 transition-transform"
          }
        />
      </button>
      <span className="text-sm text-gray-700">{representative.status}</span>
    </div>
  );
};

export const RepresentativeDetailsTable = ({
  representatives,
  onToggleStatus,
  totalRows,
  isLoading,
  page,
  perPage,
  onChangePage,
  onChangeRowsPerPage,
  sortBy = "created_at",
  sortDir = "desc",
  onSort
}: RepresentativeDetailsTableProps) => {
  const handleSort = useCallback(
    (
      selectedColumn: TableColumn<Representative>,
      sortDirection: SortOrder
    ) => {
      const id =
        selectedColumn.id == null ? "created_at" : String(selectedColumn.id);
      const representativeSortBy = id as RepresentativeSortBy;
      onSort?.(
        representativeSortBy,
        sortDirection === "asc" ? "asc" : "desc"
      );
    },
    [onSort]
  );

  const columns: TableColumn<Representative>[] = useMemo(
    () => [
      {
        id: "entity_name",
        name: "Entity Name",
        selector: (row) => row.entityName,
        sortable: true,
        grow: 2,
        cell: (row) => (
          <Typography variant="body">{row.entityName}</Typography>
        )
      },
      {
        id: "name",
        name: "Name",
        selector: (row) => row.name,
        sortable: true,
        cell: (row) => <Typography variant="body">{row.name}</Typography>
      },
      {
        id: "title",
        name: "Title",
        selector: (row) => row.title,
        sortable: true,
        cell: (row) => <Typography variant="body">{row.title}</Typography>
      },
      {
        name: "Address",
        selector: (row) => row.address,
        sortable: false,
        grow: 2,
        cell: (row) => (
          <Typography variant="body" className="text-gray-600">
            {row.address}
          </Typography>
        )
      },
      {
        id: "created_by",
        name: "Created By",
        selector: (row) => row.createdBy,
        sortable: true
      },
      {
        id: "created_at",
        name: "Created On",
        selector: (row) => row.createdOn,
        sortable: true
      },
      {
        id: "updated_by",
        name: "Modified By",
        selector: (row) => row.modifiedBy,
        sortable: true
      },
      {
        name: "Status",
        cell: (row) => (
          <StatusToggle
            representative={row}
            onToggleStatus={onToggleStatus}
          />
        ),
        width: "180px"
      }
    ],
    [onToggleStatus]
  );

  return (
    <DataTable
      key={`${sortBy}-${sortDir}`}
      columns={columns}
      data={representatives}
      customStyles={dataTableCustomStyles}
      striped={false}
      pagination
      paginationServer
      paginationTotalRows={totalRows}
      paginationDefaultPage={page}
      paginationPerPage={perPage}
      paginationRowsPerPageOptions={[10, 25, 50]}
      paginationComponent={representativeDetailsTablePagination}
      onChangePage={onChangePage}
      onChangeRowsPerPage={onChangeRowsPerPage}
      progressPending={isLoading}
      noDataComponent={
        <div className="p-12 text-center">
          <Typography variant="muted">
            No representative details found. Add your first representative details to get started.
          </Typography>
        </div>
      }
      highlightOnHover
      responsive
      sortServer
      onSort={onSort ? handleSort : undefined}
      defaultSortFieldId={sortBy}
      defaultSortAsc={sortDir === "asc"}
    />
  );
};
